import { useMemo } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import DateRangePicker from "./components/DateRangePicker";
import { useHomeDateRange } from "./state/HomeDateRangeContext";
import { formatDurationSeconds, formatRelativeTimeMs, initialsFromName } from "./utils/dateRangeUtc";
import { useHomeOverview } from "./hooks/useHomeOverview";
import { useHomeMetrics } from "./hooks/useHomeMetrics";
import { useHomeVolume } from "./hooks/useHomeVolume";
import { useHomeStatusMix } from "./hooks/useHomeStatusMix";
import { useHomeTeam } from "./hooks/useHomeTeam";
import { useHomeActivity } from "./hooks/useHomeActivity";

function activityStyle(type) {
  const t = String(type || "");
  if (t === "handoff") return { icon: "ri-shake-hands-line", color: "#0d9488" };
  if (t === "assignment") return { icon: "ri-user-add-line", color: "#f59e0b" };
  if (t === "resolved") return { icon: "ri-checkbox-circle-line", color: "#10b981" };
  if (t === "closed") return { icon: "ri-door-closed-line", color: "#64748b" };
  return { icon: "ri-notification-3-line", color: "#64748b" };
}

function HomeContent() {
  const { startDate, endDate } = useHomeDateRange();
  const { activeWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const { overview, loading: overviewLoading, error: overviewError, refresh: refreshOverview } = useHomeOverview();
  const reportingEnabled = Boolean(activeWorkspaceId);
  const metrics = useHomeMetrics({ startDate, endDate, enabled: reportingEnabled });
  const volume = useHomeVolume({ startDate, endDate, enabled: reportingEnabled });
  const statusMix = useHomeStatusMix({ startDate, endDate, enabled: reportingEnabled });
  const team = useHomeTeam({ startDate, endDate, enabled: reportingEnabled });
  const activity = useHomeActivity({ startDate, endDate, enabled: reportingEnabled }, { limit: 12 });

  const greetingName = useMemo(() => {
    const n = overview?.agent?.name;
    if (n && String(n).trim()) return String(n).trim();
    const email = overview?.humanAgentContext?.user_email;
    if (email && String(email).includes("@")) return String(email).split("@")[0];
    return "there";
  }, [overview]);

  const liveTiles = useMemo(() => {
    if (!overview || overviewError) return null;
    const pool = overview.requestPool;
    const onlineAgents = overview.onlineAgents;
    const poolN = pool?.total_available ?? (Array.isArray(pool?.requests) ? pool.requests.length : null);
    const onlineCount = Number(onlineAgents?.online_count);
    const activeCount = Number(onlineAgents?.active_count);
    const onlineN = Number.isFinite(onlineCount) ? onlineCount : null;
    const activeN = Number.isFinite(activeCount) ? activeCount : null;
    const onlineTrend =
      onlineN != null && activeN != null && activeN > 0
        ? `${onlineN} of ${activeN} active`
        : "Live";
    return [
      {
        id: "live1",
        label: "Handoff pool (open)",
        value: poolN != null ? String(poolN) : "—",
        trend: "Live",
        trendUp: true,
        icon: "ri-inbox-line",
        color: "#0d9488",
      },
      {
        id: "live2",
        label: "Agents online",
        value: onlineN != null ? String(onlineN) : "—",
        trend: onlineTrend,
        trendUp: onlineN != null && onlineN > 0,
        icon: "ri-user-smile-line",
        color: "#10b981",
      },
    ];
  }, [overview, overviewError]);

  const kpiTiles = useMemo(() => {
    const d = metrics.data;
    if (!d) return [];
    const ch = (v) => {
      const n = Number(v);
      const x = Number.isFinite(n) ? n : 0;
      return `${x >= 0 ? "+" : ""}${x.toFixed(1)}%`;
    };
    /** CSAT from reporting is 1.0–5.0; show as 0–100% for the tile. */
    const csatPct = (s) => {
      const n = Number(s);
      if (!Number.isFinite(n) || n <= 0) return "—";
      return `${Math.min(100, Math.round((n / 5) * 1000) / 10)}%`;
    };
    return [
      {
        id: "k1",
        label: "Total conversations",
        value: String(d.dailychats?.total ?? "—"),
        trend: ch(d.dailychats?.change ?? 0),
        trendUp: (d.dailychats?.change ?? 0) >= 0,
        icon: "ri-chat-3-line",
        color: "#0d9488",
      },
      {
        id: "k2",
        label: "Resolved (range)",
        value: String(d.completion?.total_completions ?? "—"),
        trend: ch(d.completion?.change ?? 0),
        trendUp: (d.completion?.change ?? 0) >= 0,
        icon: "ri-checkbox-circle-line",
        color: "#10b981",
      },
      {
        id: "k3",
        label: "Avg handle time",
        value: formatDurationSeconds(d.resolution?.avg_resolution_time),
        trend: ch(d.resolution?.avg_resolution_time_change ?? 0),
        trendUp: (d.resolution?.avg_resolution_time_change ?? 0) <= 0,
        icon: "ri-time-line",
        color: "#f59e0b",
      },
      {
        id: "k4",
        label: "Customer satisfaction",
        value: csatPct(d.csat?.score),
        trend: ch(d.csat?.change ?? 0),
        trendUp: (d.csat?.change ?? 0) >= 0,
        icon: "ri-emotion-happy-line",
        color: "#ec4899",
      },
    ];
  }, [metrics.data]);

  const mixRows = useMemo(() => {
    const sm = statusMix.data?.status_mix;
    if (sm) {
      const r = Number(sm.resolved) || 0;
      const o = Number(sm.open) || 0;
      const p = Number(sm.pending) || 0;
      const t = r + o + p;
      if (t <= 0) return [];
      return [
        { label: "Resolved", value: Math.round((r / t) * 1000) / 10, color: "#10b981" },
        { label: "Open", value: Math.round((o / t) * 1000) / 10, color: "#0d9488" },
        { label: "Pending", value: Math.round((p / t) * 1000) / 10, color: "#f59e0b" },
      ];
    }
    const r = Number(statusMix.data?.resolved_sessions) || 0;
    const o = Number(statusMix.data?.active_sessions) || 0;
    const p = Number(statusMix.data?.in_progress_sessions) || 0;
    const t = r + o + p;
    if (t <= 0) return [];
    return [
      { label: "Resolved", value: Math.round((r / t) * 1000) / 10, color: "#10b981" },
      { label: "Open", value: Math.round((o / t) * 1000) / 10, color: "#0d9488" },
      { label: "Pending", value: Math.round((p / t) * 1000) / 10, color: "#f59e0b" },
    ];
  }, [statusMix.data]);

  const openResolvedCounts = useMemo(() => {
    const sm = statusMix.data?.status_mix;
    if (sm) return { open: sm.open ?? 0, resolved: sm.resolved ?? 0 };
    return {
      open: statusMix.data?.active_sessions ?? 0,
      resolved: statusMix.data?.resolved_sessions ?? 0,
    };
  }, [statusMix.data]);

  const volumeRows = useMemo(() => volume.data ?? [], [volume.data]);
  const maxVol = useMemo(() => {
    let m = 1;
    for (const d of volumeRows) {
      const s = (d.open || 0) + (d.resolved || 0) + (d.pending || 0);
      if (s > m) m = s;
    }
    return m;
  }, [volumeRows]);

  const noWorkspace = !workspaceLoading && !activeWorkspaceId;

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin h-full overflow-y-auto">
        <header className="ui-shell sticky top-0 z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="animate-fade-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">Overview</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Hi there,
            </h1>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
              Queue health, volume, and team throughput 
              {startDate && endDate ? (
                <span className="mt-1 block text-slate-600">
                  Selected range · {startDate} — {endDate} (UTC)
                </span>
              ) : null}
              {overviewLoading ? <span className="mt-1 block"> Loading profile…</span> : null}
              {overviewError ? (
                <span className="mt-1 block text-amber-700">
                  Live profile unavailable ({overviewError.message}).{" "}
                  <button type="button" className="font-semibold text-teal-700 underline" onClick={() => void refreshOverview()}>
                    Retry
                  </button>
                </span>
              ) : null}
              {noWorkspace ? (
                <span className="mt-1 block text-amber-800">Select a workspace to load analytics.</span>
              ) : null}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
            <DateRangePicker />
          </div>
        </header>

        <div className="space-y-8 px-6 py-8 sm:px-8">
          {liveTiles && !overviewError ? (
            <section aria-labelledby="live-metrics-heading">
              <h2 id="live-metrics-heading" className="sr-only">
                Live metrics
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {liveTiles.map((m, i) => (
                  <article
                    key={m.id}
                    className="ui-card group animate-fade-up"
                    style={{ animationDelay: `${i * 45}ms` }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-inner transition-transform duration-300 group-hover:scale-105"
                        style={{ backgroundColor: `${m.color}14` }}
                      >
                        <i className={`${m.icon} text-xl`} style={{ color: m.color }} aria-hidden />
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ${
                          m.trendUp ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60" : "bg-red-50 text-red-600 ring-1 ring-red-200/60"
                        }`}
                      >
                        {m.trend}
                      </span>
                    </div>
                    <p className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{m.value}</p>
                    <p className="mt-1 text-sm font-medium text-slate-500">{m.label}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section aria-labelledby="metrics-heading">
            <h2 id="metrics-heading" className="sr-only">
              Key metrics
            </h2>
            {metrics.error ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Metrics unavailable ({metrics.error.message}).{" "}
                <button type="button" className="font-semibold text-teal-700 underline" onClick={() => metrics.refresh()}>
                  Retry
                </button>
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {metrics.loading && !metrics.data
                ? Array.from({ length: 4 }).map((_, i) => (
                    <article key={`sk-${i}`} className="ui-card animate-pulse">
                      <div className="h-12 w-12 rounded-2xl bg-slate-200" />
                      <div className="mt-5 h-8 w-24 rounded bg-slate-200" />
                      <div className="mt-2 h-4 w-32 rounded bg-slate-100" />
                    </article>
                  ))
                : kpiTiles.map((m, i) => (
                    <article
                      key={m.id}
                      className="ui-card group animate-fade-up"
                      style={{ animationDelay: `${i * 45}ms` }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-inner transition-transform duration-300 group-hover:scale-105"
                          style={{ backgroundColor: `${m.color}14` }}
                        >
                          <i className={`${m.icon} text-xl`} style={{ color: m.color }} aria-hidden />
                        </div>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ${
                            m.trendUp ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60" : "bg-red-50 text-red-600 ring-1 ring-red-200/60"
                          }`}
                        >
                          {m.trend}
                        </span>
                      </div>
                      <p className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{m.value}</p>
                      <p className="mt-1 text-sm font-medium text-slate-500">{m.label}</p>
                    </article>
                  ))}
            </div>
          </section>

          <section className="grid grid-cols-1 gap-6 xl:grid-cols-3" aria-labelledby="volume-heading">
            <div className="ui-card xl:col-span-2">
              <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 id="volume-heading" className="text-base font-semibold text-slate-900">
                    Conversation volume
                  </h3>
                  <p className="text-xs text-slate-500">Stacked by status · selected range</p>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-slate-500">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-teal-600 shadow-sm" />
                    Open
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/35" />
                    Resolved
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/35" />
                    Pending
                  </span>
                </div>
              </div>
              {volume.error ? (
                <p className="text-sm text-amber-800">
                  {volume.error.message}{" "}
                  <button type="button" className="font-semibold text-teal-700 underline" onClick={() => volume.refresh()}>
                    Retry
                  </button>
                </p>
              ) : volume.loading && !volume.data?.length ? (
                <div className="flex h-44 animate-pulse items-end gap-2 rounded-lg bg-slate-100" />
              ) : (
                <div className="flex h-44 gap-1.5 sm:gap-2">
                  {volumeRows.map((d) => (
                    <div key={d.date || d.day} className="flex min-h-0 min-w-0 flex-1 flex-col items-center gap-2">
                      <div
                        className="flex min-h-0 w-full flex-1 items-end justify-center gap-0.5"
                        role="img"
                        aria-label={`${d.day} volume`}
                      >
                        <div
                          className="min-h-[8px] w-[30%] max-w-[18px] rounded-t-md bg-teal-600 shadow-sm transition-colors duration-300 hover:bg-teal-700 sm:w-[32%]"
                          style={{ height: `${((d.open || 0) / maxVol) * 100}%` }}
                        />
                        <div
                          className="min-h-[8px] w-[30%] max-w-[18px] rounded-t-md bg-emerald-600 shadow-sm transition-all duration-300 hover:bg-emerald-700 sm:w-[32%]"
                          style={{ height: `${((d.resolved || 0) / maxVol) * 100}%` }}
                        />
                        <div
                          className="min-h-[8px] w-[30%] max-w-[18px] rounded-t-md bg-amber-600 shadow-sm transition-all duration-300 hover:bg-amber-700 sm:w-[32%]"
                          style={{ height: `${((d.pending || 0) / maxVol) * 100}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-xs font-medium text-slate-400">{d.day}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ui-card">
              <h3 className="text-base font-semibold text-slate-900">Status mix</h3>
              <p className="mt-0.5 text-xs text-slate-500">Share of human-agent sessions in range (handoff time)</p>
              {statusMix.error ? (
                <p className="mt-4 text-sm text-amber-800">
                  {statusMix.error.message}{" "}
                  <button type="button" className="font-semibold text-teal-700 underline" onClick={() => statusMix.refresh()}>
                    Retry
                  </button>
                </p>
              ) : statusMix.loading && !statusMix.data ? (
                <div className="mt-6 space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />
                  ))}
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {mixRows.map((s) => (
                    <div key={s.label}>
                      <div className="mb-1.5 flex items-center justify-between text-xs font-medium">
                        <span className="text-slate-600">{s.label}</span>
                        <span className="tabular-nums text-slate-900">{s.value}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/60">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, s.value)}%`, backgroundColor: s.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 grid grid-cols-2 gap-3 border-t border-slate-100 pt-6">
                <div className="rounded-xl bg-slate-50/80 p-3 text-center ring-1 ring-slate-100">
                  <p className="text-2xl font-semibold tabular-nums text-slate-900">{openResolvedCounts.open}</p>
                  <p className="text-xs font-medium text-slate-500">Open (active)</p>
                </div>
                <div className="rounded-xl bg-slate-50/80 p-3 text-center ring-1 ring-slate-100">
                  <p className="text-2xl font-semibold tabular-nums text-slate-900">{openResolvedCounts.resolved}</p>
                  <p className="text-xs font-medium text-slate-500">Resolved</p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-6 lg:grid-cols-3" aria-labelledby="team-heading">
            <div className="ui-card lg:col-span-2">
              <h3 id="team-heading" className="text-base font-semibold text-slate-900">
                Team performance
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">Leaderboard for selected range</p>
              {team.error ? (
                <p className="mt-4 text-sm text-amber-800">
                  {team.error.message}{" "}
                  <button type="button" className="font-semibold text-teal-700 underline" onClick={() => team.refresh()}>
                    Retry
                  </button>
                </p>
              ) : team.loading && !team.data?.length ? (
                <ul className="mt-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <li key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  ))}
                </ul>
              ) : (
                <ul className="mt-6 space-y-3">
                  {(team.data ?? []).map((agent, idx) => {
                    const sat = Math.min(
                      100,
                      Math.round(
                        Number(
                          (agent?.qa_analysis?.total_qa_analyses ?? 0) > 0
                            ? agent?.qa_analysis?.avg_overall_score
                            : agent?.performance_score ?? agent?.metrics?.feedback_score ?? 0,
                        ),
                      ),
                    );
                    const resolved = Number(agent?.metrics?.resolved_sessions ?? 0);
                    const firstTime = formatDurationSeconds(agent?.metrics?.first_response_time);
                    const avgTime = formatDurationSeconds(agent?.metrics?.avg_response_time);
                    const slaBreaches = Number(agent?.metrics?.sla_breach_count ?? 0);
                    const name = agent?.name ?? "Agent";
                    return (
                      <li
                        key={agent.id ?? name}
                        className="flex items-center gap-4 rounded-xl border border-transparent px-2 py-2 transition-colors hover:border-slate-100 hover:bg-slate-50/60"
                      >
                        <span className="w-6 text-center text-xs font-bold text-slate-300 tabular-nums">{idx + 1}</span>
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-800 ring-2 ring-white shadow-md"
                          aria-hidden
                        >
                          {initialsFromName(name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-800">{name}</p>
                          <p className="text-xs text-slate-500">
                            First {firstTime} · Avg {avgTime}
                            {slaBreaches ? ` · ${slaBreaches} SLA` : ""}
                          </p>
                        </div>
                        <div className="hidden text-right sm:block">
                          <p className="text-sm font-bold tabular-nums text-slate-900">{resolved}</p>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Resolved</p>
                        </div>
                        <div className="w-20 shrink-0 sm:w-24">
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-teal-600 transition-all duration-500"
                              style={{ width: `${sat}%` }}
                            />
                          </div>
                          <p className="mt-1 text-right text-[10px] font-semibold text-slate-400 tabular-nums">{sat}% score</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="ui-card-static">
              <h3 className="text-base font-semibold text-slate-900">Live activity</h3>
              <p className="mt-0.5 text-xs text-slate-500">Recent workspace events</p>
              {activity.error ? (
                <p className="mt-4 text-sm text-amber-800">
                  {activity.error.message}{" "}
                  <button type="button" className="font-semibold text-teal-700 underline" onClick={() => activity.refresh()}>
                    Retry
                  </button>
                </p>
              ) : activity.loading && !activity.data?.length ? (
                <ul className="mt-5 space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
                  ))}
                </ul>
              ) : (
                <ul className="mt-5 space-y-4">
                  {(activity.data ?? []).map((a) => {
                    const st = activityStyle(a.type);
                    return (
                      <li key={a.id} className="flex gap-3">
                        <div
                          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ring-slate-200/60"
                          style={{ backgroundColor: `${st.color}12` }}
                        >
                          <i className={`${st.icon} text-base`} style={{ color: st.color }} aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-snug text-slate-700">{a.text}</p>
                          <p className="mt-1 text-xs font-medium text-slate-400">{formatRelativeTimeMs(a.event_ts)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}

export default function Home() {
  return <HomeContent />;
}
